import type { WebsiteFormKey } from '../models/website-configuration/WebsiteConfiguration.types';

/**
 * Order of the "Our services" form cards when the operator has not sorted them — the order the
 * cinema website has always rendered them in.
 */
export const DEFAULT_FORMS_ORDER: WebsiteFormKey[] = [
    'rentACinema',
    'birthdayAtTheCinema',
    'schoolScreening',
    'kidsNTeens',
    'giftCards',
    'reservation'
];

/**
 * Sorts `items` by the operator's `formsOrder` (`websiteConfiguration.formsOrder`). Forms missing
 * there follow in `DEFAULT_FORMS_ORDER`; items without a form key (e.g. the newsletter card) go last,
 * keeping their relative order.
 *
 * Its own zero-dependency module so both apps and the package's node test runner can load it.
 */
export function sortByFormsOrder<TItem>(
    items: TItem[],
    getFormKey: (item: TItem) => WebsiteFormKey | undefined,
    formsOrder?: WebsiteFormKey[]
): TItem[] {
    const order = [...(formsOrder ?? [])];
    for (const key of DEFAULT_FORMS_ORDER) {
        if (!order.includes(key)) {
            order.push(key);
        }
    }
    const rank = (item: TItem) => {
        const key = getFormKey(item);
        const index = key ? order.indexOf(key) : -1;
        return index === -1 ? Infinity : index;
    };
    return [...items].sort((a, b) => {
        const rankA = rank(a);
        const rankB = rank(b);
        return rankA === rankB ? 0 : rankA < rankB ? -1 : 1;
    });
}

/**
 * The full `formsOrder` to store after the operator moved `reorderedKeys` into a new order. Cinema
 * users only see some forms, so `reorderedKeys` can be a subset of `allKeys`: the other forms keep
 * their slots instead of being dropped from the stored order.
 */
export function mergeFormsOrder(
    allKeys: WebsiteFormKey[],
    reorderedKeys: WebsiteFormKey[],
    formsOrder?: WebsiteFormKey[]
): WebsiteFormKey[] {
    const queue = [...reorderedKeys];
    return sortByFormsOrder(allKeys, (key) => key, formsOrder).map((key) =>
        reorderedKeys.includes(key) ? (queue.shift() ?? key) : key
    );
}
